// Slices de configuracao de pasta e board do modulo de tarefas.
//
// Separado de `getters-slices.js` para ficar sob o limite de 600 linhas do
// S104. Guarda os slices ancorados na PASTA/BOARD — statuses customizados por
// pasta, arvore de pastas, sprints da pasta ativa, preferencia de colunas da
// Lista, filtros server-side e o mapa de espelhos. Os slices de DADOS
// (visao rapida, progresso, overview, assignees, tags) ficam em
// `getters-slices.js`.
//
// E reexportado pelo barrel `getters.js`, entao cada export daqui vira getter
// Vuex.
import {
  TASK_STATUS_CATEGORY,
  TASK_LIST_COLUMN_DEFINITIONS,
} from '@modules/tarefas/constants/Text'
import {
  legacyColsToPx,
  clampPx,
  defaultPxFor,
  flexStyleFor,
} from '@modules/tarefas/utils/columnSizing'

// --- Per-folder custom statuses (customizar-status-tarefas) -------------

// Higher-order getter: returns the (position-ordered) status list for a
// folder, or `[]` when not loaded yet. Defensive for cold/missing slices.
export function statusesForFolder(state) {
  return function (folderId) {
    if (!folderId) return []
    const list = state.statusesByFolder && state.statusesByFolder[folderId]
    return Array.isArray(list) ? list : []
  }
}

// Resolves the status list of the ACTIVE folder (`state.activeFolderId`).
// The board uses this to render columns / chip / filter for the current
// context. Returns `[]` when no folder is active or none loaded.
export function activeFolderStatuses(state, getters) {
  return getters.statusesForFolder(state.activeFolderId)
}

// Higher-order getter: finds a status object by id across ALL loaded folders.
// Used by the chip when only the task's `statusId` is known. O(n) over loaded
// folders — folders rarely exceed a handful of statuses each.
export function statusById(state) {
  return function (id) {
    if (id == null) return undefined
    const byFolder = state.statusesByFolder || {}
    for (const folderId of Object.keys(byFolder)) {
      const list = byFolder[folderId]
      if (!Array.isArray(list)) continue
      const found = list.find(function (s) {
        return s && s.id === id
      })
      if (found) return found
    }
    return undefined
  }
}

// Higher-order getter: the default status object for a folder (the
// `is_default` one; falls back to the lowest-position `open`-category status).
// Used to seed new tasks created in that folder (AC-15).
export function defaultStatusForFolder(state, getters) {
  return function (folderId) {
    const list = getters.statusesForFolder(folderId)
    if (!list.length) return undefined
    const explicit = list.find(function (s) {
      return s && s.isDefault === true
    })
    if (explicit) return explicit
    // Fallback: lowest-position `open`-category status (list is already
    // position-sorted, so the first open one wins).
    const open = list.find(function (s) {
      return s && s.category === TASK_STATUS_CATEGORY.OPEN
    })
    return open || list[0]
  }
}

// Higher-order getter: a task's status category resolved from any loaded
// folder. Tolerant of the transitional `task.status` literal.
export function statusCategoryById(state, getters) {
  return function (id) {
    const status = getters.statusById(id)
    return status ? status.category || null : null
  }
}

// Raw maps (used by the api cache helper / loading indicators).
export function statusesLastUpdateMap(state) {
  return state.statusesLastUpdate || {}
}

export function statusesLoadingMap(state) {
  return state.statusesLoading || {}
}

// --- Empty-state machine (Board view) ----------------------------------
// Pure getters that drive the Loading / A / B / Normal state machine
// rendered by `view-task-board.vue`. Permission-related computed values
// (canCreateFolder/canCreateSprint) intentionally live in the view —
// keeping the store free of Vue-instance coupling. Since I4 (ownership)
// they are `true` for every profissional. See REQUIREMENTS § 5.4 / § 7.

export function hasFolders(state) {
  return Array.isArray(state.folders) && state.folders.length > 0
}

// --- Árvore de pastas (pasta dentro de pasta) ---------------------------
// `state.folders` guarda só as raízes; cada nó carrega `subpastas[]` (até 3
// níveis). Quem precisa achar UMA pasta usa `folderById`; quem precisa de
// TODAS numa lista única usa `allFolders`.

// Busca em profundidade na árvore. `undefined` quando não acha — os getters
// que dependem disso aplicam o próprio default (`[]` / `false`).
function _findFolder(list, id) {
  if (!Array.isArray(list) || !list.length) return undefined
  for (const node of list) {
    if (!node) continue
    if (node.id === id) return node
    const found = _findFolder(node.subpastas, id)
    if (found) return found
  }
  return undefined
}

// Higher-order getter: `store.getters['tarefas/folderById'](id)` acha a pasta
// em QUALQUER nível. Mesmo padrão de `tagById`.
export function folderById(state) {
  return function (id) {
    if (id == null) return undefined
    return _findFolder(state.folders, id)
  }
}

// Achata a árvore em pré-ordem (mãe antes das filhas). Existe para os
// seletores que precisam de TODAS as pastas numa lista única — picker de
// lista, mover tarefa em lote, sugestão de trigger e instanciação de
// template. Sem ele, esses consumidores só enxergariam as raízes e toda
// subpasta sumiria do seletor (o usuário não conseguiria mover uma tarefa
// para uma lista dentro de subpasta).
//
// É derivado e SOMENTE-LEITURA: devolve as referências dos próprios nós da
// árvore, nunca cópias, e nada é re-aninhado a partir daqui. A árvore em
// `state.folders` continua sendo a única representação persistida.
export function allFolders(state) {
  const flat = []
  function walk(list) {
    if (!Array.isArray(list) || !list.length) return
    for (const node of list) {
      if (!node) continue
      flat.push(node)
      walk(node.subpastas)
    }
  }
  walk(state.folders)
  return flat
}

export function sprintsForActiveFolder(state, getters) {
  if (!state.activeFolderId) return []
  const folder = getters.folderById(state.activeFolderId)
  if (!folder || !Array.isArray(folder.sprints)) return []
  return folder.sprints
}

export function hasSprintsInActiveFolder(state, getters) {
  return getters.sprintsForActiveFolder.length > 0
}

// Today this aliases `state.loading`. The dedicated getter exists so future
// composition (e.g., assigneesLoading) has a single point of change without
// rippling through every consumer.
export function isInitialLoading(state) {
  return !!state.loading
}

// --- List column preference slice ---------------------------------------

const _CF_KEY_PREFIX = 'cf:'

// Lookup key -> registry descriptor, construído uma vez do single source of
// truth em constants/Text.js.
const _COLUMN_DEF_BY_KEY = {}
for (const def of TASK_LIST_COLUMN_DEFINITIONS) {
  _COLUMN_DEF_BY_KEY[def.key] = def
}

// Resolve uma chave `cf:<uuid>` da preferência contra as definições de custom
// fields do escritório (`state.customFields`). Retorna o descriptor dinâmico
// `{ key, fieldId, label, field: null, cols, type, isCustomField }` ou null
// quando o field não existe (deletado — AC-13) ou está arquivado
// (`active === false` — AC-14): nesses casos a chave é silenciosamente
// ignorada (sem erro, sem coluna). `fieldId` (uuid puro) é a chave de lookup
// em `task.customFieldValues`; `isCustomField` permite ao grid ramificar sem
// chamada de função no template (anti-padrão de re-render).
function _resolveCustomFieldColumn(key, customFields) {
  const fieldId = key.slice(_CF_KEY_PREFIX.length)
  const fields = Array.isArray(customFields) ? customFields : []
  const found = fields.find(function (f) {
    return f && f.id === fieldId
  })
  if (!found) return null
  if (found.active === false) return null
  return {
    key: key,
    fieldId: fieldId,
    label: found.name,
    field: null,
    cols: 'auto',
    // Paridade header/linha: o header consome `column.colClass` e a célula
    // cf da linha (`task-item__cf-cell`) usa estas mesmas classes — sem isso
    // as colunas desalinham em breakpoints pequenos.
    colClass: 'pr-2 d-none d-sm-flex',
    type: found.type,
    isCustomField: true,
    // Custom fields herdam resize (v3 px): compactos (64..360) como as colunas
    // built-in compactas. minPx/maxPx guiam o clamp da largura no header.
    resizable: true,
    minPx: 64,
    maxPx: 360,
  }
}

// Extrai a `key` de um item de `pref.columns`, tolerante a string-v1 OU objeto
// `{key, width}` (v3 — leitura retrocompatível, AC-W13/W14). Item inválido
// (número, objeto sem `key`, null) → null. Sem optional chaining (Babel).
function _itemKey(item) {
  if (typeof item === 'string') return item
  if (item && typeof item === 'object' && typeof item.key === 'string') return item.key
  return null
}

// Resolve a largura em PIXELS de um item de preferência contra os bounds do
// descriptor da coluna (leitura retrocompatível, contrato congelado v3):
//   - width ausente/não-numérico → null (coluna cai no default rígido/grower).
//   - width `<= 12` → valor LEGADO em cols (v2) → `legacyColsToPx` (clampado).
//   - width `>= 13` (px, inclui a faixa inalcançável 13–39) → `clampPx`.
// Conversão é só leitura — NÃO re-persiste (lazy: normaliza pra px só no
// próximo save daquela coluna). `width` não-finito é tratado como ausente.
function _resolveWidthPx(item, descriptor) {
  if (!item || typeof item !== 'object') return null
  const width = item.width
  if (typeof width !== 'number' || !Number.isFinite(width)) return null
  const minPx = descriptor && descriptor.minPx
  const maxPx = descriptor && descriptor.maxPx
  if (width <= 12) return legacyColsToPx(width, minPx, maxPx)
  return clampPx(Math.round(width), minPx, maxPx)
}

// Monta `flexStyle` sobre um descriptor a partir da largura resolvida (px ou
// null). Retorna SEMPRE um NOVO objeto (descriptors do registry são congelados
// — nunca mutar), preservando `minPx`/`maxPx`/`resizable` para o gesto do
// header. Regra grower-vs-rígido:
//   - px presente → rígido na largura `px`.
//   - sem px + `title` (grower-vs-fixo decidido aqui) → grower (ocupa o resto).
//   - sem px + demais → rígido em `defaultPxFor(descriptor)`.
function _applyFlex(descriptor, pxOrNull) {
  if (!descriptor) return descriptor
  let flexStyle
  if (pxOrNull != null) {
    flexStyle = flexStyleFor({
      px: pxOrNull,
      minPx: descriptor.minPx,
      noShrink: descriptor.noShrink,
      shrinkFloor: descriptor.shrinkFloor,
    })
  } else if (descriptor.key === 'title') {
    flexStyle = flexStyleFor({ isGrower: true, minPx: descriptor.minPx })
  } else {
    flexStyle = flexStyleFor({
      px: defaultPxFor(descriptor),
      minPx: descriptor.minPx,
      noShrink: descriptor.noShrink,
      shrinkFloor: descriptor.shrinkFloor,
    })
  }
  return { ...descriptor, flexStyle: flexStyle }
}

// Resolve uma chave persistida da preferência em um descriptor de coluna.
// Built-ins vêm do registry (chaves fixas title/actions nunca são aceitas via
// preferência); `cf:<uuid>` resolve dinamicamente; chave desconhecida → null.
function _resolvePreferenceColumn(key, customFields) {
  if (typeof key !== 'string' || !key) return null
  if (key.startsWith(_CF_KEY_PREFIX)) {
    return _resolveCustomFieldColumn(key, customFields)
  }
  const def = _COLUMN_DEF_BY_KEY[key]
  if (!def || def.fixed) return null
  return def
}

// Resolve o MEIO configurável a partir de `pref.columns` (itens string-v1 OU
// objeto {key, width}). Retorna `{ middle, fixedWidths }`: `middle` são os
// descriptors resolvidos (com `flexStyle` aplicado), na ordem salva, dedupados
// por `key`; `fixedWidths` são as larguras (PX já resolvidas) das chaves fixas
// title/actions (§9.2 — não entram no meio, apenas sobrescrevem o flex das
// fixas). A resolução px usa os bounds do PRÓPRIO descriptor da coluna.
// Resolve a largura PX de uma chave fixa (title/actions) e a registra em
// `fixedWidths` quando presente. Extraído para manter o loop principal enxuto.
function _collectFixedWidth(item, key, fixedWidths) {
  const fixedDescriptor = _COLUMN_DEF_BY_KEY[key]
  const fixedPx = _resolveWidthPx(item, fixedDescriptor)
  if (fixedPx != null) fixedWidths[key] = fixedPx
}

function _resolveMiddleFromPreference(columns, customFields) {
  const middle = []
  const fixedWidths = {}
  const seen = {}
  for (const item of columns) {
    const key = _itemKey(item)
    if (key == null || seen[key]) continue
    seen[key] = true
    if (key === 'title' || key === 'actions') {
      _collectFixedWidth(item, key, fixedWidths)
      continue
    }
    const descriptor = _resolvePreferenceColumn(key, customFields)
    if (descriptor) middle.push(_applyFlex(descriptor, _resolveWidthPx(item, descriptor)))
  }
  return { middle: middle, fixedWidths: fixedWidths }
}

// Slice expost para a UI de configuração (menu de colunas — fase 5).
export function listPreference(state) {
  return state.listPreference || { sprintId: null, columns: null, loading: false }
}

/**
 * `visibleListColumns` — ÚNICA fonte de verdade das colunas visíveis da List
 * view, na ordem de exibição: `[title]` + meio configurável + `[actions]`.
 *
 * Meio configurável:
 *   - COM preferência aplicável (`listPreference.sprintId === activeSprintId`
 *     e `columns` array): resolve cada chave na ordem salva — built-ins do
 *     registry, `cf:<uuid>` contra `state.customFields`; chaves desconhecidas
 *     (field deletado) e fields arquivados são ignorados (AC-13/14); chaves
 *     duplicadas são dedupadas defensivamente (primeira ocorrência vence).
 *   - SEM preferência (columns null) OU preferência de OUTRA sprint (race
 *     guard — fetch em voo durante troca rápida de sprint): default do
 *     registry (`defaultVisible === true`), reproduzindo as colunas atuais.
 */
export function visibleListColumns(state) {
  const pref = state.listPreference || {}
  const prefApplies =
    Array.isArray(pref.columns) &&
    pref.sprintId != null &&
    pref.sprintId === state.activeSprintId

  // `fixedWidths`: larguras (PX) das chaves fixas title/actions (§9.2) —
  // sobrescrevem só o flex das fixas, sem controlar visibilidade/ordem.
  let middle
  let fixedWidths = {}
  if (prefApplies) {
    const resolved = _resolveMiddleFromPreference(pref.columns, state.customFields)
    middle = resolved.middle
    fixedWidths = resolved.fixedWidths
  } else {
    // Default (sem preferência): cada descriptor visível recebe seu flex
    // rígido default (ou grower no caso do title, decidido em _applyFlex).
    middle = TASK_LIST_COLUMN_DEFINITIONS.filter(function (def) {
      return !def.fixed && def.defaultVisible
    }).map(function (def) {
      return _applyFlex(def, null)
    })
  }
  // `fixedWidths.title`/`.actions` são px (ou ausentes → default rígido/grower).
  const title = _applyFlex(_COLUMN_DEF_BY_KEY.title, fixedWidths.title == null ? null : fixedWidths.title)
  const actions = _applyFlex(_COLUMN_DEF_BY_KEY.actions, fixedWidths.actions == null ? null : fixedWidths.actions)
  return [title].concat(middle, [actions])
}

// --- Server-side filter slice (Board + Overview) -----------------------

export function boardFilters(state) {
  return state.boardFilters || { responsavelIds: [], clientIds: [], tagIds: [] }
}

export function overviewFilters(state) {
  return (
    state.overviewFilters || {
      startDate: null,
      endDate: null,
      responsavelIds: [],
      clientIds: [],
    }
  )
}

// `hasBoardFiltersActive` / `hasOverviewFiltersActive` drive visibility of the
// "Limpar filtros" button. Multi-select: ativo = qualquer array non-vazio.
function _hasItems(arr) {
  return Array.isArray(arr) && arr.length > 0
}

export function hasBoardFiltersActive(state) {
  const f = state.boardFilters || {}
  return _hasItems(f.responsavelIds) || _hasItems(f.clientIds) || _hasItems(f.tagIds)
}

export function hasOverviewFiltersActive(state) {
  const f = state.overviewFilters || {}
  if (f.startDate) return true
  if (f.endDate) return true
  return _hasItems(f.responsavelIds) || _hasItems(f.clientIds)
}

// Duplicate View / Espelhamento de tarefa
export function getTaskMirrors(state) {
  return function (taskId) {
    if (taskId == null) return []
    return state.taskMirrors[taskId] || []
  }
}

export function isTaskMirrored(state) {
  return function (taskId) {
    if (taskId == null) return false
    const mirrors = state.taskMirrors[taskId]
    return (mirrors && mirrors.length) > 0
  }
}
